import { BankOperationStatusesNew } from 'constants/bankOperationStatusesNew';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useRouteTabs } from 'hooks/useRouteTabs';
import { useChangeBankOperationStatus } from 'pages/Finance/RequestsBankOps/hooks/useChangeBankOperationStatus';
import { useRequestsBankOpsAvailableActions } from 'pages/Finance/RequestsBankOps/hooks/useRequestsBankOpsAvailableActions';
import { useRequestsBankOpsEditable } from 'pages/Finance/RequestsBankOps/hooks/useRequestsBankOpsEditable';
import { useShowController } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Button } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
} from 'shared/mui/ContextMenu';
import { DeleteDialog } from 'shared/mui/Dialogs';
import { ShowPage } from 'shared/react-admin/Pages/ShowPage/ShowPage';
import { BankOperation } from 'types/embedded/bankOperation';
import { Permission } from 'types/embedded/permission';

import { RequestsBankOpsShowCancelDialog } from './RequestsBankOpsShowCancelDialog/RequestsBankOpsShowCancelDialog';
import { RequestsBankOpsShowChipFields } from './RequestsBankOpsShowChipFields';
import { RequestsBankOpsShowRejectDialog } from './RequestsBankOpsShowRejectDialog/RequestsBankOpsShowRejectDialog';
import { RequestsBankOpsShowSkeleton } from './RequestsBankOpsShowSkeleton';
import { RequestsBankOpsShowTabs } from './RequestsBankOpsShowTabs/RequestsBankOpsShowTabs';

export const RequestsBankOpsShow: React.FC = () => {
  const { hasResourcePermissions } = usePermissions();
  const { record } = useShowController<BankOperation>();
  const { data = [] } = useRequestsBankOpsAvailableActions(record?.originId);
  const navigate = useNavigate();
  const { currentTab, handleTabChange } = useRouteTabs();

  const { handleCloseRecordModal, openRecordModal, handleOpenRecordModal } =
    useRecordModal<BankOperation>();
  const { changeBankOperationStatus } = useChangeBankOperationStatus();

  const {
    record: recordModalReject,
    handleOpenRecordModal: handleOpenModalReject,
    openRecordModal: openModalReject,
    handleCloseRecordModal: handleCloseModalReject,
  } = useRecordModal<BankOperation>();

  const {
    record: recordModalCancel,
    handleOpenRecordModal: handleOpenModalCancel,
    openRecordModal: openModalCancel,
    handleCloseRecordModal: handleCloseModalCancel,
  } = useRecordModal<BankOperation>();

  const { isDelete, isEdit } = useRequestsBankOpsEditable();

  const renderActions = (): React.ReactElement[] | false => {
    const actions = [];

    if (data.includes(BankOperationStatusesNew.Rejected)) {
      actions.push(
        <Button
          color="error"
          key="btn-reject"
          label="Отклонить"
          onClick={() => handleOpenModalReject(record)}
          variant="outlined"
        />,
      );
    }

    if (data.includes(BankOperationStatusesNew.Canceled)) {
      actions.push(
        <Button
          color="error"
          key="btn-cancel"
          label="Аннулировать"
          onClick={() => handleOpenModalCancel(record)}
          variant="outlined"
        />,
      );
    }

    if (data.includes(BankOperationStatusesNew.OnApproval)) {
      actions.push(
        <Button
          color="primary"
          key="btn-agreement"
          label="Отправить на согласование"
          onClick={() =>
            changeBankOperationStatus(String(record?.originId), BankOperationStatusesNew.OnApproval)
          }
          variant="contained"
        />,
      );
    }

    if (data.includes(BankOperationStatusesNew.Approved)) {
      actions.push(
        <Button
          color="primary"
          key="btn-agree"
          label="Согласовать"
          onClick={() =>
            changeBankOperationStatus(String(record?.originId), BankOperationStatusesNew.Approved)
          }
          variant="contained"
        />,
      );
    }

    return actions.length ? actions : false;
  };

  return (
    <ShowPage
      actions={
        <ContextMenu key="context-menu-button">
          {hasResourcePermissions(Resources.RequestBankOperations, [
            Permission.Full,
            Permission.Update,
          ]) &&
            isEdit(record) && <ContextMenuEditButton />}
          {hasResourcePermissions(Resources.RequestBankOperations, [
            Permission.Full,
            Permission.Delete,
          ]) &&
            isDelete(record) && (
              <ContextMenuDeleteButton onClick={() => handleOpenRecordModal(record)} />
            )}
        </ContextMenu>
      }
      listName="Заявки на банковские операции"
      rightActionsSlot={renderActions()}
      skeleton={<RequestsBankOpsShowSkeleton />}
      title="Просмотр заявки на банковскую операцию"
    >
      <RequestsBankOpsShowChipFields />
      <RequestsBankOpsShowTabs handleTabChange={handleTabChange} initialTab={currentTab} />
      <DeleteDialog
        details="Банковская операция будет удалена без возможности восстановления."
        onClose={handleCloseRecordModal}
        onSuccess={() => navigate(-1)}
        open={openRecordModal}
        record={record}
        title="Удаление банковской операции"
      />
      <RequestsBankOpsShowRejectDialog
        onClose={handleCloseModalReject}
        open={openModalReject}
        record={recordModalReject}
      />
      <RequestsBankOpsShowCancelDialog
        onClose={handleCloseModalCancel}
        open={openModalCancel}
        record={recordModalCancel}
      />
    </ShowPage>
  );
};
